import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { ChevronDown, X, AlertTriangle } from 'lucide-react';
import { qrGetPublic } from './qrPublicApi';

export const STATUS_STYLE = {
  IN_RANGE: { bg: '#E7F2EE', fg: '#0B6B55', text: 'In range' },
  EXCURSION: { bg: '#FDECEC', fg: '#B42318', text: 'Excursion' },
  AWAITING_DATA: { bg: '#F3F1EA', fg: '#6b7069', text: 'Awaiting data' },
};

export const StatusPill = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.AWAITING_DATA;
  return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold" style={{ background: s.bg, color: s.fg }} data-testid={`status-${status}`}>{s.text}</span>;
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'now');

function staticMapUrl(points) {
  if (!points.length) return null;
  const path = points.filter((_, i) => i % Math.max(1, Math.floor(points.length / 40)) === 0).map(p => `${p.lat},${p.lon}`).join('|');
  const c = points[Math.floor(points.length / 2)];
  // Key-free static tiles (OSM Germany). Swap for a paid static-map provider on prod if volume requires.
  return `https://staticmap.openstreetmap.de/staticmap.php?center=${c.lat},${c.lon}&zoom=9&size=600x240&maptype=mapnik&path=color:0x0B6B55ff|weight:3|${path}`;
}

export function LegCard({ leg, profile, isPublic }) {
  const [open, setOpen] = useState(false);
  const [readings, setReadings] = useState(null);
  useEffect(() => {
    if (open && leg.visible && leg.readings_url && !readings) qrGetPublic(leg.readings_url.replace('/api', '')).then(r => setReadings(r.readings)).catch(() => setReadings([]));
  }, [open, leg, readings]);
  const gps = (readings || []).filter(r => r.lat != null);
  return (
    <li className="relative pl-6" data-testid={`leg-${leg.binding_id}`}>
      <span className="absolute left-0 top-4 w-3 h-3 rounded-full border-2 bg-white" style={{ borderColor: leg.label ? '#0B6B55' : '#9a9e96' }} />
      <div className="rounded-xl bg-white border p-4" style={{ borderColor: '#E5E3DC' }}>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm">{leg.device_type}{leg.visible && leg.trk_id ? <span className="font-mono font-normal text-slate-500"> · {leg.trk_id}</span> : ''}</span>
          {leg.label && <span className="text-xs text-emerald-700">{leg.label}</span>}
          <span className="ml-auto"><StatusPill status={leg.status} /></span>
        </div>
        {!isPublic && <p className="text-xs text-slate-500 mt-1">{leg.visible ? leg.custodian_tenant_name : 'Another custodian (not visible to you)'}</p>}
        <p className="text-xs text-slate-500 mt-1">{fmt(leg.bound_at)} → {fmt(leg.unbound_at)}{leg.visible && leg.unbound_reason ? ` · ${leg.unbound_reason.replace('MANUAL:', 'manual: ')}` : ''}
          {!isPublic && leg.visible && leg.bind_method === 'TYPED' && <span className="ml-2 inline-block px-1.5 py-px rounded text-[10px] font-semibold uppercase tracking-wide" style={{ background: '#FBF3DE', color: '#7A5C13' }} title="Code was typed in by hand — no camera scan" data-testid={`leg-typed-${leg.binding_id}`}>typed</span>}
        </p>
        {leg.provisional && <p className="text-[11px] mt-1" style={{ color: '#8A6D1F' }} data-testid={`leg-provisional-${leg.binding_id}`}>provisional — awaiting device sync</p>}
        {!isPublic && leg.visible && leg.clock_flag && (
          <p className="text-[11px] mt-1 flex items-center gap-1" style={{ color: '#B42318' }} data-testid={`leg-clock-warning-${leg.binding_id}`}>
            <AlertTriangle size={12} /> Device clock drift flagged — timestamps may be off by more than 5 min
          </p>
        )}
        {!isPublic && leg.visible && leg.lockbox && (
          <div className="text-[11px] mt-2 space-y-0.5 font-mono" data-testid={`leg-lockbox-${leg.binding_id}`}>
            <p data-testid={`leg-lockbox-weight-${leg.binding_id}`}>weight {leg.lockbox.weight_now != null ? `${leg.lockbox.weight_now} kg` : '—'}</p>
            {leg.lockbox.events.slice(-6).map((e, i) => <p key={`${e.at}-${i}`} className="text-slate-500">{new Date(e.at).toLocaleTimeString()} {e.type}{e.lid_open ? ' lid-open' : ''}{e.weight_kg != null ? ` ${e.weight_kg} kg` : ''}</p>)}
          </div>
        )}
        {!isPublic && leg.visible && !leg.lockbox && leg.summary && leg.summary.reading_count > 0 && (
          <p className="text-[11px] text-slate-600 mt-1 font-mono" data-testid={`leg-summary-${leg.binding_id}`}>
            {leg.summary.min_c.toFixed(1)}–{leg.summary.max_c.toFixed(1)} °C · {leg.summary.reading_count} readings · {leg.summary.excursion_count} excursion{leg.summary.excursion_count === 1 ? '' : 's'} · {leg.summary.minutes_out_of_range} min out
          </p>
        )}
        {!isPublic && leg.visible && !leg.lockbox && (
          <button data-testid={`leg-expand-${leg.binding_id}`} onClick={() => setOpen(o => !o)} className="mt-2 text-xs flex items-center gap-1" style={{ color: '#0B6B55' }}>
            <ChevronDown size={14} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} /> {open ? 'Hide' : 'Temperature & route'}
          </button>
        )}
        {open && (
          <div className="mt-3 space-y-3">
            {readings === null ? <p className="text-xs text-slate-400">Loading…</p> : readings.length === 0 ? <p className="text-xs text-slate-400">No readings in this window yet.</p> : (
              <div className="h-40" data-testid={`leg-chart-${leg.binding_id}`}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={readings.map(r => ({ ...r, t: new Date(r.t).getTime() }))}>
                    <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} tickFormatter={v => new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} fontSize={10} />
                    <YAxis fontSize={10} width={28} domain={['auto', 'auto']} />
                    <Tooltip labelFormatter={v => new Date(v).toLocaleString('en-IN')} formatter={v => [`${v} °C`, 'temp']} />
                    {profile && <ReferenceLine y={profile.min_c} stroke="#B42318" strokeDasharray="3 3" />}
                    {profile && <ReferenceLine y={profile.max_c} stroke="#B42318" strokeDasharray="3 3" />}
                    <Line type="monotone" dataKey="temp" stroke="#0B6B55" dot={false} strokeWidth={1.5} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            {gps.length > 0 && <img src={staticMapUrl(gps)} alt="GPS trail" className="w-full rounded-lg border" style={{ borderColor: '#E5E3DC' }} data-testid={`leg-map-${leg.binding_id}`} />}
          </div>
        )}
      </div>
    </li>
  );
}

/** One-shot camera scan (device QR for "Bind to a device", passport QR for "Set parent"). */
export function ScanOnce({ title, onCode, onClose, manualLabel = 'Type the code instead', manualPlaceholder = 'Code' }) {
  const ref = useRef(null);
  const [camErr, setCamErr] = useState('');
  const [typed, setTyped] = useState('');
  useEffect(() => {
    const s = new Html5Qrcode('scan-once-reader');
    ref.current = s;
    const safeStop = () => { try { if (s.isScanning) return s.stop().catch(() => {}); } catch { /* not running */ } return Promise.resolve(); };
    s.start({ facingMode: 'environment' }, { fps: 8, qrbox: 220 }, (t) => { safeStop(); onCode(t); }, () => {})
      .catch((e) => setCamErr(`Camera unavailable (${e?.message || e}) — type the code below`));
    return () => { safeStop(); };
  }, [onCode]);
  const submit = (e) => { e.preventDefault(); const v = typed.trim(); if (v) onCode(v); };
  return (
    <div className="fixed inset-0 z-50 bg-slate-950 text-white flex flex-col" data-testid="scan-once">
      <div className="flex items-center justify-between px-4 py-3 text-sm"><span>{title}</span><button onClick={onClose} data-testid="scan-once-close"><X size={18} /></button></div>
      <div id="scan-once-reader" className="mx-auto w-full max-w-sm aspect-square bg-black rounded-xl overflow-hidden" />
      {camErr && <p className="text-center text-xs text-amber-300 px-4 pt-2" data-testid="scan-once-camera-error">{camErr}</p>}
      <form onSubmit={submit} className="mx-auto w-full max-w-sm px-4 pt-3 flex gap-2" data-testid="scan-once-manual">
        <input value={typed} onChange={e => setTyped(e.target.value.toUpperCase())} placeholder={manualPlaceholder} aria-label={manualLabel} autoCapitalize="characters" spellCheck={false}
          className="flex-1 h-10 rounded-md bg-slate-900 border border-slate-700 px-3 font-mono text-sm text-white placeholder:text-slate-500" data-testid="scan-once-manual-input" />
        <button type="submit" disabled={!typed.trim()} className="h-10 px-4 rounded-md bg-emerald-500 text-slate-950 font-semibold text-sm disabled:opacity-40" data-testid="scan-once-manual-submit">Use code</button>
      </form>
      <p className="text-center text-[11px] text-slate-500 pt-1">{manualLabel}</p>
    </div>
  );
}
